import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Divider,
  Tabs,
  Button,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  DialogContentText,
  Grid,
  InputLabel,
  DialogActions,
  List,
  Switch,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Modal,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import GrainIcon from "@mui/icons-material/Grain";
import TimelineIcon from '@mui/icons-material/Timeline';
import { useTheme as useAppTheme } from '../../../context/ThemeContext';
import Viewer from './PressureExamViewer';
import './Css/PatientProfilePanel.css';
import { useDesignStore } from 'context/DesignContext';
import CreatePatientDialog from 'components/Dialog/createPatient';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { useApi } from 'context/ApiProvider';
import { SavePatientOnServer } from 'services/designService';
import { VOICE_SAVE_PATIENT_EVENT } from 'Voice/voiceCommands';
import { PatientConsditions } from 'models/PatientConditions';
import { ConditionSide, InsoleType, backendInsoleTypeMap } from 'models/Insole';
import { Patient } from 'models/Patient';
import { useViewerStore } from 'context/ViewerContext';
import { MeasurementSystem } from 'components/Viewer3d/consts';
import { convertKgToViewerUnit, convertMmToViewerUnit, LengthUnit, WeightUnit } from 'Utils/MeasurementUtils';


interface PatientDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
}


type ConditionsDTO = {
  footSide: 'Left' | 'Right';
  footType: InsoleType | null;
  archIndex: number | null;
  heelSpur: boolean;
  metatarsalgia: boolean;
  diabetic: boolean;
  halluxLimitus: boolean;
  sesamoiditis: boolean;
};


const PatientDetailPanel: React.FC<PatientDetailPanelProps> = ({ isOpen, onClose }) => {
  const [animationClass, setAnimationClass] = useState('');
  const { isDarkMode } = useAppTheme();
  /**
   * Phones and tablets (anything the app does not treat as desktop) get a
   * full-screen sheet: horizontal tabs on top, one column of content, and a
   * single scroll container. Desktop (>= 1024px) keeps the windowed dialog with
   * the vertical tab rail.
   */
  const isNarrowLayout = useMediaQuery('(max-width: 1023px)');
  /**
   * Portrait phones stack into one column. A phone in landscape is short but
   * wide, so it keeps the side-by-side arrangement (and the slim vertical tab
   * rail, which costs no vertical space) instead of stretching one column
   * across the screen.
   */
  const isStackedLayout = useMediaQuery('(max-width: 599px)');
  const patient = useDesignStore((state) => state.patient);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [clinicalnote, setclinicalNote] = React.useState<string>("");
  const api = useApi();
  const designId = useSelectedDesignStore((state) => state.designId);
  const [leftCond, setLeftCond] = useState<ConditionsDTO | null>(null);
  const [rightCond, setRightCond] = useState<ConditionsDTO | null>(null);
  const [origLeftCond, setOrigLeftCond] = useState<ConditionsDTO | null>(null);
  const [origRightCond, setOrigRightCond] = useState<ConditionsDTO | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | { type: 'switchTab' | 'close'; nextTab?: number }>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [savingConds, setSavingConds] = React.useState(false);
  const tabTitles = ["Pressure", "Patient Info"];
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const savePatientRef = useRef<() => Promise<void>>(async () => {});
  const hasPressure = useSelectedDesignStore((state) => state.hasPressure);

  const isOpenCVReady = useViewerStore((state) => state.isOpenCVReady);
  const [isInitialized, setIsInitialized] = useState(false);
  const measurementSystem: MeasurementSystem = useViewerStore(state => state.measurementSystem);
  const defaultLengthUnits: Record<MeasurementSystem, LengthUnit> = {
      [MeasurementSystem.metric]: 'cm',
      [MeasurementSystem.imperial]: 'inch',
  };
  const getDefaultLengthUnit = () => {
      return defaultLengthUnits[measurementSystem];
  }
  const defaultWeightUnits: Record<MeasurementSystem, WeightUnit> = {
      [MeasurementSystem.metric]: 'kg',
      [MeasurementSystem.imperial]: 'lb',
  };
  const getDefaultWeightUnit = () => {
      return defaultWeightUnits[measurementSystem];
  }


  const eqConds = (a: ConditionsDTO | null, b: ConditionsDTO | null) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return toFootTypeSelectValue(a.footType) === toFootTypeSelectValue(b.footType)
      && Number(a.archIndex ?? -1) === Number(b.archIndex ?? -1)
      && Boolean(a.heelSpur) === Boolean(b.heelSpur)
      && Boolean(a.metatarsalgia) === Boolean(b.metatarsalgia)
      && Boolean(a.diabetic) === Boolean(b.diabetic)
      && Boolean(a.halluxLimitus) === Boolean(b.halluxLimitus)
      && Boolean(a.sesamoiditis) === Boolean(b.sesamoiditis);
  };

  const condDirty = React.useMemo(() => {
      return !eqConds(leftCond, origLeftCond) || !eqConds(rightCond, origRightCond);
    }, [leftCond, rightCond, origLeftCond, origRightCond]);

  const noteDirty = React.useMemo(() => {
    return (clinicalnote ?? "") !== (patient?.clinicalNote ?? "");
  }, [clinicalnote, patient]);

  const isDirty = condDirty || noteDirty || hasUnsavedChanges;


  const discardChanges = (after?: () => void) => {
    if (condDirty) {
      setLeftCond(origLeftCond ? { ...origLeftCond } : null);
      setRightCond(origRightCond ? { ...origRightCond } : null);

      useDesignStore.setState((state: any) => {
        const d = state.insoleDesign;
        if (!d) return state;

        const newInsoles = d.insoles.map((i: any) => {
          if (i.side === 'left')
            return { ...i, conditions: origLeftCond ? { ...origLeftCond } : null };
          if (i.side === 'right')
            return { ...i, conditions: origRightCond ? { ...origRightCond } : null };
          return i;
        });

        return { ...state, insoleDesign: { ...d, insoles: newInsoles } };
      });
    }

    if (noteDirty) {
      setclinicalNote(patient?.clinicalNote ?? "");
    }

    setHasUnsavedChanges(false);
    setConfirmOpen(false);
    setPendingAction(null);

    after?.();
};



  const handleTabChangeGuarded = (_e: React.SyntheticEvent, next: number) => {
if(hasPressure){
      if (isDirty) {
      setPendingAction({ type: 'switchTab', nextTab: next });
      setConfirmOpen(true);
    } else {
      setActiveTab(next);
    }
}
  };

  const handleCloseGuarded = () => {
    if (isDirty) {
      setPendingAction({ type: 'close' });
      setConfirmOpen(true);
    } else {
      onClose();
    }
  };

  function toFootTypeSelectValue(value?: InsoleType | string | null): InsoleType {
    if (!value) return InsoleType.Standard;
    if (backendInsoleTypeMap[value as InsoleType]) return backendInsoleTypeMap[value as InsoleType] as InsoleType;
    const compact = String(value).toLowerCase().replace(/[\s_-]/g, "");
    const match = Object.entries(InsoleType).find(([key, label]) =>
      key.toLowerCase() === compact || String(label).toLowerCase().replace(/[\s_-]/g, "") === compact
    );
    return (match?.[0] ?? "Standard") as InsoleType;
  }

  function normalizeCond(raw: PatientConsditions, side: 'Left' | 'Right'): ConditionsDTO {
    return {
      footSide: side,
      footType: toFootTypeSelectValue(side == 'Right' ? raw.insoleTemplateRight : raw.insoleTemplateLeft),
      archIndex: side == 'Right' ? raw.archIndexRight : raw.archIndexLeft,
      heelSpur: convertConditionSideToBool(raw.heelSpur, side),
      metatarsalgia: convertConditionSideToBool(raw.metatarsalgia, side),
      diabetic: convertConditionSideToBool(raw.diabetic, side),
      halluxLimitus: convertConditionSideToBool(raw.halluxLimitus, side),
      sesamoiditis: convertConditionSideToBool(raw.sesamoiditis, side),
    };
  }

useEffect(() => {
  if (!isOpen) return;

  if (patient && !isInitialized) {
    // Existing patient → initialize from it (once)
    const conditions = patient.patientConditions;

    const LC = normalizeCond(conditions, 'Left');
    const RC = normalizeCond(conditions, 'Right');

    setLeftCond(LC);
    setRightCond(RC);
    setOrigLeftCond(LC);
    setOrigRightCond(RC);

    setclinicalNote(patient.clinicalNote ?? "");
    setIsInitialized(true);
  }

  // Always stop loading once panel is open (whether patient exists or not)
  setLoading(false);
}, [isOpen, patient, isInitialized]);

useEffect(() => {
  if (!isOpen) {
    setAnimationClass('patient-panel-hidden');
    setLoading(true);
    setIsInitialized(false);
    setHasUnsavedChanges(false);
  }
}, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setActiveTab(hasPressure ? 0 : 1);
  }, [hasPressure, isOpen])

  useEffect(() => {
    const applyFromStore = (detail?: { clinicalNote?: string }) => {
      const current = useDesignStore.getState().patient;
      const note = detail?.clinicalNote ?? current?.clinicalNote ?? "";
      setclinicalNote(note);
      if (current?.patientConditions) {
        const LC = normalizeCond(current.patientConditions, 'Left');
        const RC = normalizeCond(current.patientConditions, 'Right');
        setLeftCond(LC);
        setRightCond(RC);
      }
      setHasUnsavedChanges(true);
      setIsInitialized(true);
    };
    const onVoiceUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      setIsCreateOpen(false);
      if (detail.saved) {
        setHasUnsavedChanges(false);
        return;
      }
      applyFromStore(detail);
    };
    const onVoiceOpen = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.close || detail.create || detail.closeCreate) {
        setIsCreateOpen(false);
      }
      if (detail.close || detail.create) return;
      if (detail.tab === "info") setActiveTab(1);
    };
    const onVoiceSave = () => { void savePatientRef.current(); };
    document.addEventListener("voice:patientUpdated", onVoiceUpdate);
    document.addEventListener("voice:openPatient", onVoiceOpen as EventListener);
    document.addEventListener(VOICE_SAVE_PATIENT_EVENT, onVoiceSave);
    return () => {
      document.removeEventListener("voice:patientUpdated", onVoiceUpdate);
      document.removeEventListener("voice:openPatient", onVoiceOpen as EventListener);
      document.removeEventListener(VOICE_SAVE_PATIENT_EVENT, onVoiceSave);
    };
  }, []);

  const toInitialValues = (p: Patient | null | undefined) => {
    if (!p) return undefined;
    return {
      name: p.name ?? '',
      gender: (p.gender ?? 'Male') as 'Male' | 'Female',
      age: p.age ?? null,
      weight: p.weight ?? null,
      height: p.height ?? null,
    } as const;
  };

  function mapForApi() {
  const updatedConditions = patient?.patientConditions
    ? { ...patient.patientConditions }
    : new PatientConsditions();
  
  updatedConditions.insoleTemplateLeft = toFootTypeSelectValue(leftCond?.footType);
  updatedConditions.insoleTemplateRight = toFootTypeSelectValue(rightCond?.footType);
  updatedConditions.archIndexLeft = leftCond?.archIndex ?? -1;
  updatedConditions.archIndexRight = rightCond?.archIndex ?? -1;
  updatedConditions.halluxLimitus = convertBoolToConditionSide(
    !!leftCond?.halluxLimitus,
    !!rightCond?.halluxLimitus
  );
  updatedConditions.heelSpur = convertBoolToConditionSide(
    !!leftCond?.heelSpur,
    !!rightCond?.heelSpur
  );
  updatedConditions.metatarsalgia = convertBoolToConditionSide(
    !!leftCond?.metatarsalgia,
    !!rightCond?.metatarsalgia
  );
  updatedConditions.sesamoiditis = convertBoolToConditionSide(
    !!leftCond?.sesamoiditis,
    !!rightCond?.sesamoiditis
  );
  updatedConditions.diabetic = convertBoolToConditionSide(
    !!leftCond?.diabetic,
    !!rightCond?.diabetic
  );

  useDesignStore.setState((state: any) => ({
    ...state,
    patient: { 
      ...state.patient,
      patientConditions: updatedConditions 
    },
    patientConditionUpdated: true,
  }));

  const autoPropertiesJson = {
    insoleTemplateRight: updatedConditions.insoleTemplateRight,
    insoleTemplateLeft: updatedConditions.insoleTemplateLeft,
    archIndexRight: updatedConditions.archIndexRight,
    archIndexLeft: updatedConditions.archIndexLeft,
    metatarsalgia: updatedConditions.metatarsalgia,
    halluxLimitus: updatedConditions.halluxLimitus,
    sesamoiditis: updatedConditions.sesamoiditis,
    heelSpur: updatedConditions.heelSpur,
    diabetic: updatedConditions.diabetic,
  };

  return autoPropertiesJson;
}
  function convertBoolToConditionSide(aleft: boolean, aright: boolean): ConditionSide {
    if (aleft && !aright) return ConditionSide.left;
    else if (!aleft && aright) return ConditionSide.right;
    else if (aleft && aright) return ConditionSide.both;
    return ConditionSide.none;
  }

  function convertConditionSideToBool(item: ConditionSide, side: 'Left' | 'Right'): boolean {
    let res = false;
    if (side == 'Left' && (item == ConditionSide.left || item == ConditionSide.both))
      res = true;
    if (side == 'Right' && (item == ConditionSide.right || item == ConditionSide.both))
      res = true;
    return res;
  }


  const handleSaveConditions = async () => {
  setSaveError(null);

  if (!designId) { setSaveError('No design selected.'); return; }

  const data = {
    autoProperties: mapForApi(),
  };

  setSavingConds(true);
  try {
    let res = await api.patch(`/api/designs/${designId}`, data);
    if (!res.ok) {
      if (res.status === 401) {
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
        return;
      }
      throw new Error(res?.body?.message || `Save failed (HTTP ${res.status})`);
    }

    const updated = Patient.cloneFrom(patient || new Patient());
    updated.clinicalNote = clinicalnote || useDesignStore.getState().patient?.clinicalNote || "";
    if (leftCond && rightCond) {
      updated.patientConditions.insoleTemplateLeft = leftCond.footType as InsoleType;
      updated.patientConditions.insoleTemplateRight = rightCond.footType as InsoleType;
      updated.patientConditions.archIndexLeft = leftCond.archIndex ?? -1;
      updated.patientConditions.archIndexRight = rightCond.archIndex ?? -1;
      updated.patientConditions.halluxLimitus = convertBoolToConditionSide(
        !!leftCond.halluxLimitus,
        !!rightCond.halluxLimitus
      );
      updated.patientConditions.heelSpur = convertBoolToConditionSide(
        !!leftCond.heelSpur,
        !!rightCond.heelSpur
      );
      updated.patientConditions.metatarsalgia = convertBoolToConditionSide(
        !!leftCond.metatarsalgia,
        !!rightCond.metatarsalgia
      );
      updated.patientConditions.sesamoiditis = convertBoolToConditionSide(
        !!leftCond.sesamoiditis,
        !!rightCond.sesamoiditis
      );
      updated.patientConditions.diabetic = convertBoolToConditionSide(
        !!leftCond.diabetic,
        !!rightCond.diabetic
      );
    }
    await SavePatientOnServer(api, updated, designId);
    useDesignStore.setState({ patient: updated });

    if (res.body?.patient?.patientConditions) {
      const conditions = res.body.patient.patientConditions;
      const LC = normalizeCond(conditions, 'Left');
      const RC = normalizeCond(conditions, 'Right');
      
      setLeftCond(LC);
      setRightCond(RC);
      setOrigLeftCond(LC);
      setOrigRightCond(RC);
    } else {
      const newLeftCond = leftCond ? { ...leftCond } : null;
      const newRightCond = rightCond ? { ...rightCond } : null;
      
      setLeftCond(newLeftCond);
      setRightCond(newRightCond);
      setOrigLeftCond(newLeftCond);
      setOrigRightCond(newRightCond);
    }

    setHasUnsavedChanges(false);
    setConfirmOpen(false);
    setPendingAction(null);
    onClose();
  } catch (e: any) {
    setSaveError(e?.message || 'Failed to save conditions.');
  } finally {
    setSavingConds(false);
  }
};
  savePatientRef.current = handleSaveConditions;


  if (!isOpen && animationClass === 'patient-panel-hidden') {
    return null;
  }

  function FootCard({
    title,
    isDarkMode,
    cond,
    setCond,
  }: {
    title: string;
    isDarkMode: boolean;
    cond: ConditionsDTO | null;
    setCond: React.Dispatch<React.SetStateAction<ConditionsDTO | null>>;
  }) {
    const labelColor = isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)';
    const labelBaseColor = isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(17,24,39,0.75)';
    const disabledLabel = isDarkMode ? 'rgba(255,255,255,0.38)' : 'rgba(17,24,39,0.38)';
    const focusBorderColor = 'var(--accent-color)'; // same you used for borders
    const handle = <K extends keyof ConditionsDTO>(k: K, v: ConditionsDTO[K]) => {
      setHasUnsavedChanges(true);
      setCond(prev => (prev
        ? { ...prev, [k]: v }
        : { footSide: title.startsWith('Left') ? 'Left' : 'Right', footType: InsoleType.Standard, archIndex: null, heelSpur: false, metatarsalgia: false, diabetic: false, halluxLimitus: false, sesamoiditis: false, [k]: v } as ConditionsDTO));
    };

    return (
      <Box
        sx={{
          p: isNarrowLayout ? 1.25 : 2.5,
          height: isNarrowLayout ? 'auto' : '100%',
          borderRadius: isNarrowLayout ? 2 : 3,
          bgcolor: isDarkMode ? '#2D2D2D' : '#ffffff',
          border: '1px solid',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
        }}
      >
        <Typography
          fontWeight={800}
          sx={{ mb: isNarrowLayout ? 0.75 : 1.5, color: labelColor, fontSize: isNarrowLayout ? '0.9rem' : undefined }}
        >
          {title}
        </Typography>

        <Grid container spacing={isNarrowLayout ? 1 : 2} sx={{ mb: isNarrowLayout ? 0 : 1 }}>
          <Grid item xs={12} sm={6} size={isStackedLayout ? 7 : (isNarrowLayout ? 12 : undefined)}>
            <FormControl sx={{
              minWidth: isNarrowLayout ? 0 : 220,
              width: isNarrowLayout ? '100%' : { xs: '100%', sm: 'auto' },
              color: labelColor,

              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode ? 'rgba(255,255,255,0.28)' : 'rgba(16,24,40,0.28)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(16,24,40,0.48)',
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--accent-color)',
                borderWidth: '2px',
              },
            }} size="small">
              <InputLabel sx={{ color: labelColor }}>Foot Type</InputLabel>
              <Select
                label="Foot Type"
                sx={{
                  color: labelColor,
                  '& .MuiSvgIcon-root': {
                    color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)',
                  }
                }}
                value={cond?.footType ?? 'Standard'}
                onChange={(e) => handle('footType', e.target.value as any)}
                MenuProps={{ sx: { zIndex: 9999 } }}
              >
                <MenuItem value="Standard">Standard</MenuItem>
                <MenuItem value="PlantarFasciitis">Plantar Fasciitis</MenuItem>
                <MenuItem value="PesPlanusMild">Pes Planus (Mild)</MenuItem>
                <MenuItem value="PesPlanusModerate">Pes Planus (Moderate)</MenuItem>
                <MenuItem value="PesPlanusSevere">Pes Planus (Severe)</MenuItem>
                <MenuItem value="PesPlanusVerySevere">Pes Planus (Very Severe)</MenuItem>
                <MenuItem value="PesCavus">Pes Cavus</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} size={isStackedLayout ? 5 : (isNarrowLayout ? 12 : undefined)}>
            <TextField
              label="Arch Index"
              type="number"
              size="small"
              fullWidth
              InputLabelProps={{
                sx: {
                  color: labelBaseColor,
                  '&.Mui-focused': { color: focusBorderColor },
                  '&.Mui-disabled': { color: disabledLabel },
                  '&.MuiInputLabel-shrink': { color: labelBaseColor },
                },
              }}
              sx={{
                color: labelColor,
                '& .MuiInputBase-input': {
                  color: labelColor,
                },

                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.28)' : 'rgba(16,24,40,0.28)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.48)' : 'rgba(16,24,40,0.48)',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--accent-color)',
                  borderWidth: '2px',
                },

                '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(16,24,40,0.18)',
                },
              }}
              value={cond?.archIndex ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : Number(e.target.value);
                handle('archIndex', (Number.isFinite(v as any) ? (v as number) : null) as any);
              }}
              inputProps={{ min: 0, max: 30 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: isNarrowLayout ? 0.5 : 1 }} />

        {
          [
            { k: 'heelSpur', label: 'Heel Spur', icon: <LocalHospitalIcon /> },
            { k: 'metatarsalgia', label: 'Metatarsalgia', icon: <ArrowDownwardIcon /> },
            { k: 'diabetic', label: 'Diabetic', icon: <HealthAndSafetyIcon /> },
            { k: 'halluxLimitus', label: 'Hallux Limitus', icon: <DirectionsWalkIcon /> },
            { k: 'sesamoiditis', label: 'Sesamoiditis', icon: <GrainIcon /> },
          ].map(({ k, label, icon }) => (
            <Box key={k} display="flex" alignItems="center" justifyContent="space-between" py={isNarrowLayout ? 0 : 0.75}>
              <Box display="flex" alignItems="center" gap={isNarrowLayout ? 0.75 : 1.25} sx={{ minWidth: 0 }}>
                <Box sx={{
                  color: 'var(--accent-color)',
                  display: 'flex',
                  '& svg': { fontSize: isNarrowLayout ? 18 : 24 },
                }}>{icon}</Box>
                <Typography sx={{
                  color: labelColor,
                  fontWeight: 600,
                  fontSize: isNarrowLayout ? '.82rem' : '.95rem',
                  whiteSpace: 'nowrap',
                }}>{label}</Typography>
              </Box>
              <Switch
                size={isNarrowLayout ? 'small' : 'medium'}
                checked={Boolean((cond as any)?.[k])}
                onChange={(e) => handle(k as any, e.target.checked as any)}
              />
            </Box>
          ))
        }
      </Box >
    );
  }

  return (
    <Modal open={isOpen} onClose={handleCloseGuarded} closeAfterTransition sx={{display: "flex", alignItems: "center", justifyContent: "center",}}>
      <Box
        sx={{
          bgcolor: isDarkMode ? '#333' : '#fff',
          color: isDarkMode ? '#fff' : '#000',
          ...(isNarrowLayout
            ? { borderRadius: 0, width: '100%', height: '100%', maxHeight: '100%' }
            : { borderRadius: 2, width: { xs: '95vw', md: '70vw' }, maxHeight: '95vh' }),
          mx: 'auto',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          position: 'relative',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            p: isNarrowLayout ? 1 : 2,
            borderBottom: "1px solid #ccc",
          }}
        >
          <Typography
            variant={isNarrowLayout ? "subtitle1" : "h6"}
            sx={{ color: isDarkMode ? "#fff" : "#076596", fontWeight: isNarrowLayout ? 700 : undefined }}
          >
            {tabTitles[activeTab]}
          </Typography>
          <IconButton onClick={onClose} size={isNarrowLayout ? "small" : "medium"}>
            <CloseIcon sx={{ color: isDarkMode ? "#fff" : "#000" }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{
          display: "flex",
          flexDirection: isStackedLayout ? "column" : "row",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
        }}>
          {/* Tab rail: vertical on desktop, a full-width strip on mobile */}
          <Box
            sx={{
              flexShrink: 0,
              ...(isStackedLayout
                ? { width: "100%", minWidth: 0, borderBottom: "1px solid #ccc" }
                : { width: "5%", minWidth: "38px", borderRight: "1px solid #ccc" }),
              bgcolor: isDarkMode ? "#2D2D2D" : "#fafafa",
            }}
          >
            <Tabs
              orientation={isStackedLayout ? "horizontal" : "vertical"}
              variant={isStackedLayout ? "fullWidth" : "standard"}
              value={activeTab}
              onChange={handleTabChangeGuarded}
              sx={{
                minHeight: isStackedLayout ? 44 : undefined,
                "& .MuiTabs-flexContainer": {
                  alignItems: "center",
                },
                "& .MuiTab-root": {
                  minWidth: 0,
                  minHeight: isStackedLayout ? 44 : 70,
                  padding: isStackedLayout ? "4px 8px" : undefined,
                  fontSize: isStackedLayout ? "0.78rem" : undefined,
                  textTransform: isStackedLayout ? "none" : undefined,
                  display: "flex",
                  flexDirection: isStackedLayout ? "row" : "column",
                  gap: isStackedLayout ? 0.75 : 0,
                  justifyContent: "center",
                  alignItems: "center",
                  color: isDarkMode ? "#fff" : "#000", 
                  "& svg": {
                    color: isDarkMode ? "#fff" : "#000",
                  },
                  "& .MuiTab-iconWrapper": {
                    margin: isStackedLayout ? 0 : undefined,
                  },
                  "&.Mui-selected": {
                    color: "var(--accent-color)",
                    "& svg": {
                      color: "var(--accent-color)", 
                    },
                  },
                },
                "& .MuiTabs-indicator": {
                  ...(isStackedLayout
                    ? { bottom: 0, height: 3 }
                    : { left: 0, width: 3 }),
                  backgroundColor: "var(--accent-color)",
                },
              }}
              >
              {hasPressure && <Tab
                data-tour-id="patient-tab-pressure"
                label={isStackedLayout ? "Pressure" : undefined}
                icon={<TimelineIcon sx={{ fontSize: isStackedLayout ? 20 : 26 }} />}
                iconPosition="start"                
              />}
              <Tab
                data-tour-id="patient-tab-info"
                disabled={!hasPressure}
                label={isStackedLayout ? "Patient Info" : undefined}
                icon={<PersonIcon sx={{ fontSize: isStackedLayout ? 20 : 26 }} />}
                iconPosition="start"                
              />
            </Tabs>
          </Box>

          {/* Main scrollable content -- the only scroll container on mobile */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            p: 0,
          }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "400px",
                  color: "var(--accent-color)",
                }}
              >
                <CircularProgress color="inherit" />
              </Box>
            ) : (
              <Paper
                sx={{
                  backgroundColor: isDarkMode ? "#333333" : "#f7f9fb",
                  boxShadow: "none",
                  p: 0,
                  flexGrow: 1,
                  width: isNarrowLayout ? '100%' : 'fit-content',
                  minWidth: 0,
                }}
                className='patient-detail-panel-paper'
              >
                {/* Pressure Test Tab */}
                {activeTab === 0 &&
                  (isOpenCVReady ? (
                    <Viewer />
                  ) : (
                    <div style={{ color: "var(--text-color)" }}>Loading OpenCV Library...</div>
                  ))}

                {/* Patient Info Tab */}
                {activeTab === 1 && (
                  <>
                    <Box
                      sx={{
                        p: isNarrowLayout ? 0.75 : { xs: 1, md: 2 },
                        borderRadius: 1,
                        bgcolor: isDarkMode ? '#2D2D2D' : '#fff',
                        border: '1px solid',
                        borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        position: 'relative',
                        boxSizing: 'border-box',
                        mb: 0.5, 
                        margin: isNarrowLayout ? 0 : 0.5
                      }}
                    >
                      <Grid container spacing={isNarrowLayout ? 1 : 6}>
                        {/* Patient Summary */}
                        <Grid item xs={12} md={4} size={isStackedLayout ? 12 : (isNarrowLayout ? 4 : undefined)}>
                          <Box
                            sx={{
                              p: isNarrowLayout ? 1.25 : 2.5,
                              borderRadius: isNarrowLayout ? 2 : 3,
                              height: isNarrowLayout ? 'auto' : '100%',
                              bgcolor: isDarkMode ? '#2D2D2D' : '#ffffff',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                            }}
                          >
                            <Box
                              display="flex"
                              flexDirection={isNarrowLayout ? 'row' : 'column'}
                              alignItems="center"
                              gap={isNarrowLayout ? 1.25 : 2}
                              sx={{ mb: isNarrowLayout ? 1 : 3, width: isNarrowLayout ? '100%' : 'auto' }}
                            >
                              <Avatar sx={{ width: isNarrowLayout ? 42 : 72, height: isNarrowLayout ? 42 : 72 }}>
                                <PersonIcon fontSize={isNarrowLayout ? 'medium' : 'large'} />
                              </Avatar>
                              <Box sx={{ minWidth: 0, textAlign: isNarrowLayout ? 'left' : 'center' }}>
                                <Typography sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }} fontWeight={700}>
                                  {patient?.name ?? '—'}
                                </Typography>
                              </Box>
                            </Box>

                            <Grid container spacing={isNarrowLayout ? 1 : 5} sx={{ mt: isNarrowLayout ? 0 : 2, pl: isNarrowLayout ? 0 : { xs: 2, md: 3 } }}>
                              <Grid item xs={4} size={isNarrowLayout ? 4 : undefined}>
                                <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }}>Age</Typography>
                                <Typography sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }} fontWeight={600}>
                                  {patient?.age ?? '—'}
                                </Typography>
                              </Grid>

                              <Grid item xs={4} size={isNarrowLayout ? 4 : undefined}>
                                <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }}>Height</Typography>
                                <Typography sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }} fontWeight={600}>
                                  {patient?.height != null ? `${measurementSystem === MeasurementSystem.metric ? convertMmToViewerUnit(patient.height) / 10 : convertMmToViewerUnit(patient.height)} ${getDefaultLengthUnit()}` : '—'}
                                </Typography>
                              </Grid>

                              <Grid item xs={4} size={isNarrowLayout ? 4 : undefined}>
                                <Typography variant="body2" sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }}>Weight</Typography>
                                <Typography sx={{ color: isDarkMode ? 'rgba(212,212,212,0.9)' : 'var(--accent-color)' }} fontWeight={600}>
                                  {patient?.weight != null ? `${convertKgToViewerUnit(patient.weight)} ${getDefaultWeightUnit()}` : '—'}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Button
                              fullWidth
                              variant="outlined"
                              size={isNarrowLayout ? 'small' : 'medium'}
                              onClick={() => setIsCreateOpen(true)}
                              sx={{
                                mt: isNarrowLayout ? 1.25 : 3,
                                borderRadius: 2,
                                borderColor: 'var(--accent-color)',
                                color: 'var(--accent-color)',
                              }}
                            >
                              {patient ? 'Edit Patient' : 'Create Patient'}
                            </Button>
                          </Box>
                        </Grid>
                        {/* Foot Cards */}
                        {patient && (
                          <>
                            <Grid item xs={12} md={4} size={isStackedLayout ? 12 : (isNarrowLayout ? 4 : undefined)}>
                              <FootCard
                                title="Left Foot"
                                isDarkMode={isDarkMode}
                                cond={leftCond}
                                setCond={setLeftCond}
                              />
                            </Grid>
                            <Grid item xs={12} md={4} size={isStackedLayout ? 12 : (isNarrowLayout ? 4 : undefined)}>
                              <FootCard
                                title="Right Foot"
                                isDarkMode={isDarkMode}
                                cond={rightCond}
                                setCond={setRightCond}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                      {patient && (
                        <TextField
                          placeholder="Clinical Notes..."
                          value={clinicalnote ?? ""}
                          onChange={e => {
                            setHasUnsavedChanges(true);
                            setclinicalNote(e.target.value);
                          }}
                          sx={{
                            mt: isNarrowLayout ? 1.25 : 4,
                            '& .MuiInputBase-input': {
                              fontSize: isNarrowLayout ? '0.85rem' : undefined,
                              color: isDarkMode ? 'rgba(255,255,255,0.92)' : 'rgba(17,24,39,0.92)',
                              caretColor: isDarkMode ? '#fff' : '#111827',
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(17,24,39,0.45)',
                              opacity: 1,
                            },
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: isDarkMode ? 'rgba(255,255,255,0.28)' : 'rgba(16,24,40,0.24)',
                              },
                              '&:hover fieldset': {
                                borderColor: isDarkMode ? 'rgba(255,255,255,0.44)' : 'rgba(16,24,40,0.44)',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'var(--accent-color)',
                                borderWidth: '1.5px',
                              },
                            },
                          }}
                          fullWidth
                          multiline
                          minRows={isStackedLayout ? 3 : (isNarrowLayout ? 2 : 9)}
                        />
                    )}
                    </Box>
                  </>
                )}
              </Paper>
            )}
          </Box>
          {/* Dialog Components - Outside Modal but in same return */}
          <CreatePatientDialog
            open={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            onSave={async (updated?: Patient) => {
              if (updated) {
                // Always update if we have a new/updated patient
                if (patient == null) {
                  updated.patientConditions = new PatientConsditions();
                } else {
                  updated.patientConditions = patient.patientConditions; // preserve conditions
                  updated.clinicalNote = patient.clinicalNote;
                }

                useDesignStore.setState({ patient: updated });
                await SavePatientOnServer(api, updated, designId);
              }
              setIsCreateOpen(false);
              }}
            initial={toInitialValues(patient)}
            zIndex={3000}
          />
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Conditions have changed. Do you want to save them?
              </Typography>
              {saveError && (
                <Typography variant="body2" sx={{ mt: 1, color: '#ff6b6b' }}>
                  {saveError}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  const next = pendingAction;
                  discardChanges(() => {
                    if (next?.type === 'switchTab' && typeof next.nextTab === 'number') {
                      setActiveTab(next.nextTab);
                    } else if (next?.type === 'close') {
                      onClose();
                    }
                  });
                }}
                disabled={savingConds}
                variant="text"
                 sx={{
                  color: '#1c78d3',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.08)',
                  },
                  '&.Mui-disabled': {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                  },
                  border: 'none',
                  backgroundColor: 'transparent',
                  textTransform: 'none',
                  fontWeight: 400,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveConditions}
                disabled={savingConds}
                variant="text"
                 sx={{
                  color: '#1c78d3',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.08)',
                  },
                  '&.Mui-disabled': {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                  },
                  border: 'none',
                  backgroundColor: 'transparent',
                  textTransform: 'none',
                  fontWeight: 400,
                }}
              >
                {savingConds ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: isNarrowLayout ? 1 : 2,
            flexShrink: 0,
            borderTop: "1px solid #ccc",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: isDarkMode ? "#fff" : "#000",
              borderColor: "#aaa",
              width: "90px",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>

          {/* Footer Tab 0 */}
          {activeTab === 0 && (
            <Button
              variant="contained"
              onClick={handleCloseGuarded}
              sx={{
                color: "#fff",
                backgroundColor: "#076596",
                width: "90px",
                fontWeight: 500,
                "&:hover": { backgroundColor: "#0a6fb4" },
              }}
            >
              OK
            </Button>
          )}

          {/* Footer Tab 1 */}
          {activeTab === 1 && (
            <Button
              variant="contained"
              disabled={!isDirty}
              onClick={async () => {
                await handleSaveConditions();
                onClose();
              }}
              sx={{
                color: "#fff",
                backgroundColor: isDirty ? "#076596" : "#999",
                width: "90px",
                fontWeight: 500,
                "&:hover": isDirty ? { backgroundColor: "#0a6fb4" } : {},
              }}
            >
              Save
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
export default PatientDetailPanel;